// src/plugins/loader.ts
// The thing the app calls: install / uninstall / enable / disable / load / reload.
// Pure TypeScript, no React — testable in node.
import { getEntry, loadRegistry } from './registry';
import { getState, setState, removeState, listStates } from './storage';
import { PluginNotFoundError } from './types';
/** Install a plugin. Seeds its config from the manifest default. Idempotent. */
export function install(id) {
    const entry = getEntry(id);
    if (!entry)
        throw new PluginNotFoundError(id);
    const existing = getState(id);
    if (existing?.installed)
        return existing;
    const next = {
        installed: true,
        enabled: true,
        config: (entry.manifest.defaultConfig ?? {}),
        installedAt: new Date().toISOString(),
    };
    setState(id, next);
    return next;
}
/** Remove a plugin completely. Drops its config. Idempotent. */
export function uninstall(id) {
    removeState(id);
}
export function enable(id) {
    return toggle(id, true);
}
export function disable(id) {
    return toggle(id, false);
}
function toggle(id, enabled) {
    const entry = getEntry(id);
    if (!entry)
        throw new PluginNotFoundError(id);
    const existing = getState(id);
    const next = existing ?? {
        installed: true, enabled, config: (entry.manifest.defaultConfig ?? {}),
    };
    next.enabled = enabled;
    setState(id, next);
    return next;
}
/** The full set of installed plugins right now — manifest + state, components not yet loaded. */
export function listInstalled() {
    const out = [];
    const states = listStates();
    for (const [id, state] of Object.entries(states)) {
        if (!state.installed)
            continue;
        const entry = getEntry(id);
        if (!entry)
            continue; // plugin folder removed — skip without crashing
        out.push({ manifest: entry.manifest, state });
    }
    return out;
}
/** List of every manifest available to install (installed or not). */
export function listAvailable() {
    return Object.values(loadRegistry()).map(e => e.manifest);
}
/** Lazy-load the component + handlers for a plugin. Returns null if uninstalled/disabled. */
export async function load(id) {
    const entry = getEntry(id);
    if (!entry)
        return null;
    const state = getState(id);
    if (!state?.installed || !state.enabled)
        return null;
    let Component;
    if (entry.loadComponent) {
        try {
            const mod = await entry.loadComponent();
            Component = mod.default;
        }
        catch (e) {
            console.error(`[plugins] failed to load component for "${id}":`, e);
        }
    }
    let handlers;
    if (entry.loadHandlers) {
        try {
            const mod = await entry.loadHandlers();
            handlers = mod.handlers ?? mod.default;
        }
        catch (e) {
            console.error(`[plugins] failed to load handlers for "${id}":`, e);
        }
    }
    return { manifest: entry.manifest, state, Component, handlers };
}
/** Build a HandlerContext from current state. The host passes this to every handler. */
export function contextFor(id, orgId) {
    const entry = getEntry(id);
    const state = getState(id);
    return {
        pluginId: id,
        orgId,
        config: (state?.config ?? entry?.manifest.defaultConfig ?? {}),
        permissions: entry?.manifest.permissions ?? [],
    };
}
